import React, { FC, useEffect, useImperativeHandle, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tab, Tabs, Form as FormBootstrap, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import Label from '../../../../../components/library/inputs/label/Label';
import ModalComponent from '../../../../../components/modal/ModalComponent';
import { CultureTextModel, FormModel } from '../../../../../models/form/form';
import FormStepAdmin from './FormStepAdmin';
import { RootState } from '../../../../../store';
import { addStep, deleteStep, setSelectedStepById, updateStep } from '../../../../../slices/formadmin';
import { StepModel } from '../../../../../models/form/step';
import { verifyFieldsStep } from '../rules/Rules';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../../../../models/security/security';
import PaymentModes from '../paymentModes/PaymentModes';
import { FormikProps } from 'formik';
import DiscountCodes from '../discountCodes/DiscountCodes';
import CultureText from '../../../../../components/culture/CultureText';
import CultureTextInput from '../../../../../components/culture/CultureTextInput';
import { getText } from '../../../../../utilities/CultureUtilities';

const Form: FC<any> = React.forwardRef<{
  getFormikRefs: () => any;
}>((props, ref) => {
  const [canDeleteSection, setCanDeleteSection] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [stepEdition, setStepEdition] = useState<StepModel | undefined>(undefined);
  const [stepName, setStepName] = useState<CultureTextModel[] | string>('');
  const [formSecurity, setFormSecurity] = useState<SecurityModel | null>(null);
  const form = useSelector((state: RootState) => state.formAdmin.form) as FormModel;
  const selectedStep = useSelector((state: RootState) => state.formAdmin.selectedStep);
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const selectedLanguage = useSelector((state: RootState) => state.formAdmin.selectedLanguage) as string;
  const localizations = useSelector((state: RootState) => state.formAdmin.form?.localizations) as string[];
  const intl = useIntl();
  const dispatch = useDispatch();

  useEffect(() => {
    if (security) {
      setFormSecurity(getSecuritySection(security, SecuritySections.FORM));
    }
  }, [security]);

  const onClickNewStep = () => {
    setStepName('');
    setStepEdition(undefined);
    setOpenSettings(true);
  };

  const confirmDelete = () => {
    if (stepEdition !== undefined) {
      setOpenSettings(false);
      setOpenDelete(false);
      dispatch(deleteStep(stepEdition));
    }
  };

  const addNewStep = () => {
    dispatch(addStep(stepName));
    setOpenSettings(false);
  };

  const editStep = () => {
    if (stepEdition !== undefined) {
      dispatch(updateStep({ id: stepEdition.id, name: stepName }));
    }
    setOpenSettings(false);
  };

  const onClickStepSettings = (step: StepModel) => {
    verifyCanDeleteStep(step);
    setOpenSettings(true);
    setStepEdition(step);
    setStepName(step.name);
  };

  const verifyCanDeleteStep = (step: StepModel) => {
    if (step !== undefined) {
      const fieldsInRules = verifyFieldsStep(step, form.content.rules);
      setCanDeleteSection(!fieldsInRules);
    } else {
      setCanDeleteSection(false);
    }
  };

  const handleOpenDeleteModal = () => {
    setOpenDelete(true);
    setOpenSettings(false);
  };

  const handleCloseDeleteModal = () => {
    setOpenDelete(false);
    setOpenSettings(true);
  };

  const renderTooltip = (text) => <Tooltip id="button-tooltip">{text}</Tooltip>;

  const paymentModesRef = useRef<FormikProps<any>>(null);
  const discountCodesRef = useRef<FormikProps<any>>(null);

  useImperativeHandle(ref, () => ({
    getFormikRefs: () => ({
      paymentModesRef,
      discountCodesRef,
    }),
  }));

  if (!form) {
    return null;
  }

  return (
    <div className="form-admin">
      <ModalComponent
        closeSettings={() => setOpenSettings(false)}
        buttons={[
          {
            text: intl.formatMessage({ id: 'multiSteps.cancel' }),
            onClick: () => setOpenSettings(false),
            buttonVariant: 'secondary',
          },
          {
            text: intl.formatMessage({ id: 'multiSteps.save' }),
            onClick: () => {
              if (stepEdition === undefined) {
                addNewStep();
              } else {
                editStep();
              }
            },
            buttonVariant: 'primary',
          },
        ]}
        modalHeading={intl.formatMessage({
          id: stepEdition === undefined ? 'multiSteps.addStep' : 'multiSteps.editStep',
        })}
        show={openSettings}
      >
        <div className="d-flex flex-column">
          <FormBootstrap.Group className="input-md justify-content-center m-4">
            <Label label={intl.formatMessage({ id: 'multiSteps.stepName' })} />
            <CultureTextInput
              id={`step.${stepEdition?.id}.name`}
              culture={selectedLanguage}
              value={stepName}
              localizations={localizations}
              onChange={(e) => setStepName(e)}
            />
          </FormBootstrap.Group>
          {stepEdition !== undefined && (
            <FormBootstrap.Group>
              <Label label={intl.formatMessage({ id: 'multiSteps.deleteStep' })} />
              <div>
                {canDeleteSection && (
                  <Button variant="danger" onClick={handleOpenDeleteModal}>
                    {intl.formatMessage({ id: 'multiSteps.delete' })}
                  </Button>
                )}
                {!canDeleteSection && (
                  <OverlayTrigger
                    placement="right"
                    delay={{ show: 50, hide: 100 }}
                    defaultShow={false}
                    overlay={renderTooltip(intl.formatMessage({ id: 'multiSteps.deleteRule' }))}
                  >
                    <span className="d-inline-block">
                      <Button variant="danger" disabled style={{ pointerEvents: 'none' }}>
                        {intl.formatMessage({ id: 'multiSteps.delete' })}
                      </Button>
                    </span>
                  </OverlayTrigger>
                )}
              </div>
            </FormBootstrap.Group>
          )}
        </div>
      </ModalComponent>
      {stepEdition !== undefined && (
        <ModalComponent
          closeSettings={handleCloseDeleteModal}
          buttons={[
            {
              text: intl.formatMessage({ id: 'multiSteps.cancel' }),
              buttonVariant: 'secondary',
              onClick: handleCloseDeleteModal,
            },
            {
              text: intl.formatMessage({ id: 'multiSteps.delete' }),
              buttonVariant: 'danger',
              onClick: confirmDelete,
            },
          ]}
          show={openDelete}
          modalHeading={intl.formatMessage({
            id: 'multiSteps.deleteModalHeader',
          })}
          modalSize="lg"
        >
          <div className="d-flex justify-content-center flex-column">
            <span>
              <FormattedMessage id="multiSteps.deleteConfirmation" />
            </span>
            <span className="field-text">{getText(stepEdition.name, intl.locale)}</span>
          </div>
        </ModalComponent>
      )}
      <Tabs
        activeKey={selectedStep}
        onSelect={(k) => {
          if (k !== null && k !== 'add-step') {
            dispatch(setSelectedStepById(k));
          }
        }}
        className="mb-3"
        style={{ borderBottom: '1px solid #dee2e6', marginRight: 0, marginLeft: 0 }}
      >
        {form.content.steps &&
          form.content.steps.length > 0 &&
          form.content.steps.map((step, index) => {
            return (
              <Tab
                eventKey={step.id}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', fontSize: 14 }}>
                    <div>
                      {index + 1}. <CultureText culture={selectedLanguage} text={step.name}></CultureText>
                    </div>
                    {formSecurity?.update && (
                      <div style={{ marginLeft: 8 }}>
                        <OverlayTrigger
                          delay={{ show: 50, hide: 100 }}
                          overlay={renderTooltip(intl.formatMessage({ id: 'multiSteps.editStep' }))}
                          placement="top"
                        >
                          <span
                            onClick={() => onClickStepSettings(step)}
                            className="mdi mdi-pencil-outline"
                            style={{ fontSize: 18 }}
                          ></span>
                        </OverlayTrigger>
                      </div>
                    )}
                  </div>
                }
                key={step.id}
              >
                <FormStepAdmin fieldProperties={form.fieldProperties} rules={form.content.rules} step={step} />
              </Tab>
            );
          })}
        {form.paymentEnabled && (
          <Tab eventKey="payment-settings" title={<span>Payment</span>}>
            <PaymentModes ref={paymentModesRef}></PaymentModes>
            <DiscountCodes ref={discountCodesRef}></DiscountCodes>
          </Tab>
        )}
        {formSecurity?.update && (
          <Tab
            eventKey="add-step"
            title={
              <div onClick={onClickNewStep}>
                <span className="mdi mdi-plus" style={{ fontSize: 18 }}></span>
              </div>
            }
          ></Tab>
        )}
      </Tabs>
    </div>
  );
});
Form.displayName = 'Form';

export default Form;
